/**
 * CatalogFilters — Katalog Ürünleri gelişmiş filtre diyaloğu.
 *
 * Taslak (draft) deseni InventoryFilters'tan alınmıştır: diyalog açıldığı anda uygulanan
 * filtreler taslağa kopyalanır ve kullanıcı "Uygula"ya basana kadar liste değişmez. Aksi
 * halde her seçimde 23.186 kayıtlık katalog yeniden sorgulanır.
 *
 * Süper admin paneli i18n kullanmaz; metinler panelin geri kalanıyla tutarlı düz Türkçe.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import PercentIcon from '@mui/icons-material/Percent';
import StraightenIcon from '@mui/icons-material/Straighten';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';

import CatalogService from '../../../../services/CatalogService';
import {
  CATALOG_UNIT_OPTIONS,
  CATALOG_VAT_RATE_OPTIONS,
  emptyCatalogFilterDraft,
} from '../types/catalog.types';
import type {
  CatalogCategory,
  CatalogFilterDraft,
  CatalogSector,
  CatalogStatusFilter,
} from '../types/catalog.types';

const STATUS_OPTIONS: { value: CatalogStatusFilter; label: string }[] = [
  { value: 'all', label: 'Tümü' },
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Pasif' },
];

export interface CatalogFiltersProps {
  open: boolean;
  onClose: () => void;
  /** Uygula: taslağı ana state'e yazar ve listeyi yeniler. */
  onApply: (draft: CatalogFilterDraft) => void;
  /** Diyalog açılırken taslağın başlangıcı — şu an uygulanan filtreler. */
  applied: CatalogFilterDraft;
  sectors: CatalogSector[];
}

/**
 * Düz kategori listesini üst → altları sırasıyla dizer. Autocomplete tek seviyeli bir
 * liste beklediği için hiyerarşi girinti ile gösterilir.
 */
function flattenHierarchy(categories: CatalogCategory[]): CatalogCategory[] {
  const roots = categories.filter((c) => !c.parentId);
  const childrenOf = new Map<number, CatalogCategory[]>();
  categories.forEach((c) => {
    if (!c.parentId) return;
    if (!childrenOf.has(c.parentId)) childrenOf.set(c.parentId, []);
    childrenOf.get(c.parentId)!.push(c);
  });

  const ordered: CatalogCategory[] = [];
  roots.forEach((root) => {
    ordered.push(root);
    (childrenOf.get(root.id) ?? []).forEach((child) => ordered.push(child));
  });

  // Üstü silinmiş/pasifleşmiş bir alt kategori kökler arasında görünmez; kaybolmasın
  // diye sona eklenir.
  categories.forEach((c) => {
    if (!ordered.some((o) => o.id === c.id)) ordered.push(c);
  });

  return ordered;
}

export const CatalogFilters: React.FC<CatalogFiltersProps> = ({
  open,
  onClose,
  onApply,
  applied,
  sectors,
}) => {
  const [draft, setDraft] = useState<CatalogFilterDraft>(applied);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Diyalog açıldığında uygulanan filtreleri taslağa kopyala.
  useEffect(() => {
    if (open) setDraft({ ...applied });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Kademeli kategori: sektör seçilince yalnızca o sektörün kategorileri çekilir.
  // Sektör yokken kategori listesi de yoktur — 249 kategoriyi sektörsüz göstermek
  // kullanıcıya hangi sektöre ait olduğunu söylemez.
  useEffect(() => {
    if (!open) return;

    const sectorId = draft.sector?.id;
    if (!sectorId) {
      setCategories([]);
      return;
    }

    let cancelled = false;
    setCategoriesLoading(true);
    CatalogService.getCategories(sectorId)
      .then((rows) => {
        if (!cancelled) setCategories(rows);
      })
      .catch((error) => {
        console.error('Kategoriler yüklenemedi:', error);
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, draft.sector?.id]);

  const orderedCategories = useMemo(() => flattenHierarchy(categories), [categories]);

  const patch = <K extends keyof CatalogFilterDraft>(key: K, value: CatalogFilterDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const draftChips = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (draft.sector) {
      chips.push({
        key: 'sector',
        label: `Sektör: ${draft.sector.name}`,
        // Sektör kalkınca kategori anlamsız kalır.
        clear: () => setDraft((prev) => ({ ...prev, sector: null, category: null })),
      });
    }
    if (draft.category) {
      chips.push({
        key: 'category',
        label: `Kategori: ${draft.category.name}`,
        clear: () => patch('category', null),
      });
    }
    if (draft.unit) {
      chips.push({ key: 'unit', label: `Birim: ${draft.unit}`, clear: () => patch('unit', null) });
    }
    if (draft.vatRate !== null) {
      chips.push({
        key: 'vat',
        label: `KDV: %${draft.vatRate}`,
        clear: () => patch('vatRate', null),
      });
    }
    if (draft.status !== 'all') {
      chips.push({
        key: 'status',
        label: `Durum: ${draft.status === 'active' ? 'Aktif' : 'Pasif'}`,
        clear: () => patch('status', 'all'),
      });
    }
    return chips;
  }, [draft]);

  const handleApply = () => {
    onApply({ ...draft });
    onClose();
  };

  // Autocomplete menüsü Dialog dışına portal edilmesin — seçim kaybını önler.
  const acSlotProps = { popper: { disablePortal: true } };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      disableRestoreFocus
      PaperProps={{ sx: { borderRadius: 2, maxHeight: { xs: '92vh', sm: '85vh' } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, pb: 1 }}>
        <FilterListIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
          Gelişmiş Filtreler
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {draftChips.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {draftChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={chip.clear}
                />
              ))}
            </Box>
            <Divider sx={{ mt: 1.5 }} />
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              size="small"
              options={sectors}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={draft.sector}
              onChange={(_, newValue) => {
                // Sektör değişince önceki kategori artık başka sektöre ait olur.
                setDraft((prev) => ({ ...prev, sector: newValue, category: null }));
              }}
              slotProps={acSlotProps}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                return (
                  <li key={option.id} {...optionProps}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                        {option.name}
                      </Typography>
                      {option.productCount > 0 && (
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                          {option.productCount.toLocaleString('tr-TR')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sektör"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <StorefrontIcon sx={{ fontSize: 16 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              size="small"
              disabled={!draft.sector}
              loading={categoriesLoading}
              options={orderedCategories}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={draft.category}
              onChange={(_, newValue) => patch('category', newValue)}
              slotProps={acSlotProps}
              noOptionsText="Bu sektörde kategori yok"
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                const isChild = !!option.parentId;
                return (
                  <li key={option.id} {...optionProps}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: isChild ? 2 : 0 }}
                    >
                      {isChild ? (
                        <SubdirectoryArrowRightIcon
                          sx={{ fontSize: 15, mr: 0.5, color: 'text.disabled' }}
                        />
                      ) : (
                        <CategoryIcon
                          sx={{ fontSize: 15, mr: 0.5, color: 'primary.main', opacity: 0.7 }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isChild ? 400 : 600, flex: 1 }}
                        noWrap
                      >
                        {option.name}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={draft.sector ? 'Kategori' : 'Kategori (önce sektör seçin)'}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <CategoryIcon sx={{ fontSize: 16 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 260 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              options={[...CATALOG_UNIT_OPTIONS]}
              value={draft.unit}
              onChange={(_, newValue) => patch('unit', newValue)}
              slotProps={acSlotProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Birim"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <StraightenIcon sx={{ fontSize: 16 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              options={CATALOG_VAT_RATE_OPTIONS}
              getOptionLabel={(option) => `%${option}`}
              value={draft.vatRate}
              onChange={(_, newValue) => patch('vatRate', newValue)}
              slotProps={acSlotProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="KDV Oranı"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <PercentIcon sx={{ fontSize: 16 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              options={STATUS_OPTIONS}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={STATUS_OPTIONS.find((o) => o.value === draft.status) ?? STATUS_OPTIONS[0]}
              onChange={(_, newValue) => patch('status', newValue ? newValue.value : 'all')}
              slotProps={acSlotProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Durum"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <ToggleOnIcon sx={{ fontSize: 16 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button color="inherit" variant="text" onClick={onClose}>
          İptal
        </Button>
        <Button
          color="error"
          variant="text"
          startIcon={<ClearAllIcon />}
          onClick={() => setDraft(emptyCatalogFilterDraft())}
        >
          Temizle
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={handleApply} startIcon={<FilterListIcon />}>
          Uygula
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Filtre butonunun üstündeki aktif filtre sayısı rozeti. */
export const CatalogFilterBadge: React.FC<{ count: number; children: React.ReactNode }> = ({
  count,
  children,
}) => (
  <Badge badgeContent={count > 0 ? count : 0} color="primary" invisible={count === 0}>
    {children}
  </Badge>
);

export default CatalogFilters;
